<template>
  <div
    class="special-ship-settings w-full absolute left-0 floating-panel z-10 p-4 flex flex-col"
    v-show="item != null"
    :style="{ top: posY + 'px' }"
    @mousedown="onPointerDown"
    @touchstart="onPointerDown"
  >
    <span
      v-if="itemType != null"
      class="block font-bold"
      v-text="itemType.name"
    ></span>
    <table v-if="itemType != null">
      <tr v-bind:title="$t('components.special_ship.level.description')">
        <td
          class="text-left"
          v-text="$t('components.special_ship.level.name')"
        ></td>
        <td
          class="text-right pr-1"
          v-text="
            $t('components.special_ship.level.value', {
              n: data.level,
              available: availableLevels
            })
          "
        ></td>
      </tr>
      <tr v-bind:title="$t('components.special_ship.exp.description')">
        <td
          class="text-left"
          v-text="$t('components.special_ship.exp.name')"
        ></td>
        <td class="w-20">
          <number-input
            class="w-20 text-right"
            v-model="data.exp"
            @input="onExpChanged"
          />
        </td>
      </tr>
      <tr v-bind:title="$t('components.special_ship.exp_required.description')">
        <td
          class="text-left"
          v-text="$t('components.special_ship.exp_required.name')"
        ></td>
        <td
          class="text-right pr-1"
          v-text="
            $t('components.special_ship.exp_required.value', {
              n: formatNumber(requiredExp)
            })
          "
        ></td>
      </tr>
    </table>

    <span
      class="block font-bold"
      v-text="$t('components.special_ship.boni')"
    ></span>
    <table v-if="itemType != null">
      <tr
        :key="entry.typeId"
        v-for="entry of availableBoni"
        v-bind:title="
          $t(entry.description, { n: Math.round(entry.perLevel * 100) + '%' })
        "
      >
        <td class="text-left" v-text="entry.name"></td>
        <td
          class="text-right w-20 pr-1"
          v-text="Math.round(entry.level * entry.perLevel * 100) + '%'"
        ></td>
        <td class="w-20">
          <number-input
            class="w-20 text-right"
            v-model="entry.level"
            @input="onBonusChanged(entry)"
          />
        </td>
      </tr>
    </table>

    <special-ship-import-area class="mt-2" @import="onImport" />
  </div>
</template>

<script>
import Vue from "vue";
import SpecialShipData from "@/store/model/special-ship-data";
import { mapActions, mapGetters } from "vuex";
import NumberInput from "@/components/inputs/NumberInput";
import { StringHelper } from "@/helpers/string-helper";
import SpecialShipImportArea from "@/components/Ships/SpecialShipImportArea";

export default {
  name: "SpecialShipSettings",
  components: { SpecialShipImportArea, NumberInput },
  props: {
    specialShipBus: {
      type: Vue,
      required: true
    }
  },
  data: () => {
    return {
      item: null,
      itemType: null,
      data: null,
      posY: 0,
      availableLevels: 0,
      requiredExp: 0,
      expFactor: 0,
      availableBoni: []
    };
  },
  created() {
    this.specialShipBus.$on("show", this.onShowRequested);
    document.body.addEventListener("mousedown", this.onGlobalPointerDown);
    document.body.addEventListener("touchstart", this.onGlobalPointerDown);
  },
  destroyed() {
    document.body.removeEventListener("mousedown", this.onGlobalPointerDown);
    document.body.removeEventListener("touchstart", this.onGlobalPointerDown);
  },
  computed: {
    ...mapGetters("data", ["shipTypes", "resourceTypes"])
  },
  methods: {
    ...mapActions("combat", ["markFleetsChanged"]),
    onShowRequested(event) {
      if (this.item != null) {
        this.close();
      }
      const item = event.item;
      if (item.special == null) {
        item.special = new SpecialShipData();
      }
      this.item = item;
      this.itemType = this.shipTypes[item.typeId];
      this.data = item.special;
      this.posY = event.posY || 0;
      this.onExpChanged();
      this.updateAvailableBoni();
    },
    onPointerDown(event) {
      event.stopPropagation();
    },
    close() {
      this.specialShipBus.$emit("close");
      this.item = null;
    },
    onGlobalPointerDown() {
      if (this.item == null) {
        return;
      }
      this.close();
    },
    onExpChanged() {
      const usedLevels = Object.values(this.data.levelups).reduce(
        (v, l) => v + l,
        0
      );
      let level = 0;
      let exp = this.data.exp;
      let baseRequiredExp = this.itemType.special.exp;
      let expFactor = this.itemType.special.exp_factor;
      let requiredExp = 0;
      while (exp >= requiredExp || level < usedLevels) {
        const forNextLevel = Math.ceil(
          baseRequiredExp * Math.pow(expFactor, level)
        );
        requiredExp = Math.max(0, forNextLevel - exp);
        if (requiredExp > 0 && level >= usedLevels) {
          break;
        }
        exp = Math.max(exp, requiredExp);
        level++;
      }
      this.requiredExp = Math.ceil(requiredExp);
      this.data.exp = exp;
      this.data.level = level;
      this.availableLevels = Math.max(0, level - usedLevels);
    },
    onBonusChanged(entry) {
      this.item.special.setBonusLevel(entry.typeId, entry.level);
      this.onExpChanged();
      this.markFleetsChanged();
    },
    onImport(data) {
      if (data === null) {
        return;
      }
      this.data.reset();
      this.data.level = data.level;
      this.data.exp = data.exp;
      Object.keys(data.levelups).forEach(typeId => {
        this.data.setBonusLevel(typeId, data.levelups[typeId]);
      });
      this.onExpChanged();
      this.updateAvailableBoni();
      this.markFleetsChanged();
    },
    updateAvailableBoni() {
      if (this.itemType == null || this.itemType.special == null) {
        this.availableBoni = [];
        return;
      }
      const boniPerLevel = this.itemType.special.boni_per_level;
      const availableBoni = [];
      const resourceTypes = this.resourceTypes;
      for (let typeId of Object.keys(boniPerLevel)) {
        const perLevel = boniPerLevel[typeId];
        const typeIdShort = typeId.split(":")[1];
        const resourceType = resourceTypes[typeId];
        if (resourceType == null) {
          console.warn(
            "Unknown resource type " +
              typeId +
              ". Cannot load special ship bonus."
          );
          continue;
        }
        availableBoni.push({
          typeId: typeId,
          name: resourceType.name,
          description: "components.special_ship." + typeIdShort,
          perLevel: perLevel,
          level: this.item.special.getBonusLevel(typeId)
        });
      }
      this.availableBoni = availableBoni;
    },
    formatNumber(value) {
      return value >= 0 ? StringHelper.formatNumber(value) : 0;
    }
  }
};
</script>

<style scoped></style>
